<template>
  <div class="tab-container">
    <div class="tab-content">
      <!-- タイトル部分 -->
      <div class="title-section">
        <div class="title-wrapper">
          <div class="title-text">{{ $t('historiesLookBackSelectText') }}</div>
          <div class="dropdown-icon">
            <svg class="icon-svg" viewBox="0 0 16 16" fill="none">
              <g>
                <path
                  d="M8 12.1299L14.065 6.06497H1.93503L8 12.1299Z"
                  fill="#171636"
                />
              </g>
            </svg>
          </div>
        </div>
      </div>
      
      <!-- タブボタン部分 -->
      <div class="tabs-section">
        <div class="tabs-wrapper">
          <div class="tabs-container">
            <button
              v-for="year in availableYears"
              :key="year"
              :class="['tab-button', { 'tab-active': year === selectedYear }]"
              @click="selectYear(year)"
            >
              <div class="tab-content-wrapper">
                <div class="tab-year">{{ year }}</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'

@Component({})
export default class Tab extends Vue {
  @Prop({ type: Array, default: () => [] })
  availableYears!: number[]

  // 現在選択されている年（2024を初期値とする）
  selectedYear: number = 2024

  // コンポーネントがマウントされた時に初期年を設定
  mounted() {
    if (this.availableYears.length > 0) {
      // 2024が利用可能な場合は2024を、そうでなければ最新年を選択
      if (this.availableYears.includes(2024)) {
        this.selectedYear = 2024
      } else {
        this.selectedYear = Math.max(...this.availableYears)
      }
      // 初期選択を親コンポーネントに通知
      this.$emit('year-changed', this.selectedYear)
      
      // 2024が見えるように右端にスクロール
      this.$nextTick(() => {
        this.scrollToRightEnd()
      })
    }
  }

  // availableYearsが変更された時の処理
  @Watch('availableYears', { immediate: true })
  onAvailableYearsChanged(newYears: number[]) {
    if (newYears.length > 0 && this.selectedYear === 0) {
      // 2024が利用可能な場合は2024を、そうでなければ最新年を選択
      if (newYears.includes(2024)) {
        this.selectedYear = 2024
      } else {
        this.selectedYear = Math.max(...newYears)
      }
      // 初期選択を親コンポーネントに通知
      this.$emit('year-changed', this.selectedYear)
      
      // 2024が見えるように右端にスクロール
      this.$nextTick(() => {
        this.scrollToRightEnd()
      })
    }
  }

  // タブコンテナを右端にスクロールする関数
  scrollToRightEnd() {
    const tabsContainer = this.$el.querySelector('.tabs-container') as HTMLElement
    if (tabsContainer) {
      tabsContainer.scrollLeft = tabsContainer.scrollWidth - tabsContainer.clientWidth
    }
  }

  // 年を選択する関数
  selectYear(year: number) {
    this.selectedYear = year
    // 親コンポーネントにイベントを送信
    this.$emit('year-changed', year)
  }
}
</script>

<style scoped>
.tab-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
  height: 100%;
}

/* タイトル部分 */
.title-section {
  position: relative;
  flex-shrink: 0;
}

.title-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  position: relative;
}

.title-text {
  font-weight: 943;
  font-style: normal;
  font-size: 12px;
  color: #171636;
  text-align: left;
  white-space: nowrap;
  line-height: normal;
  flex-shrink: 0;
}

.dropdown-icon {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.icon-svg {
  display: block;
  width: 100%;
  height: 100%;
}

/* タブ部分 */
.tabs-section {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.tabs-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}

.tabs-container {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  position: relative;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  min-width: 0; /* flexboxでのオーバーフローを許可 */
}

/* タブがはみ出る場合のスクロール対応 */
@media (max-width: 480px) {
  .tabs-container {
    justify-content: flex-start;
  }
}

.tabs-container::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

/* タブボタン */
.tab-button {
  background: #ffffff;
  position: relative;
  border-radius: 30px;
  flex-shrink: 0;
  border: 1px solid #C6C6C6;
  cursor: pointer;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  /* cssのborderはouterのため、borderをつけ外しするとサイズが異なる
  そのためpaddingやheight値からborder分を引く */
  padding: 0 15px;
  height: 29px;
}

.tab-active {
  background: linear-gradient(to right, #a989fe 0%, #5ec3ff 50%, #54f4f1 100%);
  border: none;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  height: 29px;
}

.tab-year {
  font-weight: 600;
  font-style: normal;
  font-size: 14px;
  color: #171636;
  text-align: center;
  white-space: nowrap;
  line-height: normal;
  position: relative;
  flex-shrink: 0;
}

.tab-active .tab-year {
  color: #ffffff;
}
</style>
